'use client';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { ProcessorEditHeader } from '../../components/processor-edit-header';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { minorUnitsToMajorUnits, moneyStringOrNumberToMinorUnits } from '@/lib/money';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { NMIEditSkeleton } from '../components/nmi-edit-skeleton';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';

const AVAILABLE_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'INR'];

const CURRENCY_OPTIONS = AVAILABLE_CURRENCIES.map((currency) => ({
  label: currency,
  value: currency,
}));

const AMEX_REQUESTOR_TYPES = [
  { value: 'NONE', label: 'Not applicable' },
  { value: 'MER', label: 'MER - General Merchant' },
  { value: 'AGG', label: 'AGG - Aggregator' },
  { value: 'JCB', label: 'JCB - JCB-Acquired Merchant' },
  { value: 'OTA', label: 'OTA - Online Travel Agency' },
  { value: 'OPT', label: 'OPT - OptBlue Seller' },
  { value: 'WAL', label: 'WAL - Digital Wallet' },
];

interface NMIEditPageProps {
  processor: ProcessorDetails;
}

const NMIEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id);
  if (isLoading) {
    return <NMIEditSkeleton />;
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <NMIEditPage processor={data} />;
};

const NMIEditPage = ({ processor }: NMIEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const { toast } = useToast();
  const processorExit = useProcessorEditExit();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    baseCurrency: processor.baseCurrency || 'USD',
    supportedCurrencies: processor.supportedCurrencies || [],
    monthlyProcessingCapacity: minorUnitsToMajorUnits(
      processor.monthlyProcessingCapacity || 0,
      processor.baseCurrency || 'USD',
    ),
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    apiKey: (processor.options as any)?.apiKey || '',
    nmiProcessorId: (processor.options as any)?.nmiProcessorId || '',
    useProcessorToken: (processor.options as any)?.useProcessorToken ?? false,
    enable3DS: (processor.options as any)?.enable3DS ?? false,
    merchantName: (processor.options as any)?.merchantName || '',
    merchantUrl: (processor.options as any)?.merchantUrl || '',
    merchantCountryCode: (processor.options as any)?.merchantCountryCode || '',
    amexRequestorType:
      (processor.options as any)?.amexRequestorType === ''
        ? 'NONE'
        : (processor.options as any)?.amexRequestorType || 'NONE',
    cbSiretNumber: (processor.options as any)?.cbSiretNumber || '',
    discoverClientId: (processor.options as any)?.discoverClientId || '',
    acquirerBinVisa: (processor.options as any)?.acquirerBinVisa || '',
    acquirerBinMastercard: (processor.options as any)?.acquirerBinMastercard || '',
    acquirerBinAmex: (processor.options as any)?.acquirerBinAmex || '',
    acquirerBinDiscover: (processor.options as any)?.acquirerBinDiscover || '',
    acquirerBinJcb: (processor.options as any)?.acquirerBinJcb || '',
    acquirerBinDinersClub: (processor.options as any)?.acquirerBinDinersClub || '',
    acquirerBinCB: (processor.options as any)?.acquirerBinCB || '',
    descriptor: (processor.options as any)?.descriptor?.descriptor || '',
    mcc: (processor.options as any)?.descriptor?.mcc || '',
    merchantId: (processor.options as any)?.descriptor?.merchantId || '',
  });

  const validateAcquirerBins = () => {
    if (!formData.enable3DS) return true;

    const hasBin =
      !!formData.acquirerBinVisa ||
      !!formData.acquirerBinMastercard ||
      !!formData.acquirerBinAmex ||
      !!formData.acquirerBinDiscover ||
      !!formData.acquirerBinJcb ||
      !!formData.acquirerBinDinersClub ||
      !!formData.acquirerBinCB;

    return hasBin;
  };

  const handleSubmit = async () => {
    // Validate 3DS configuration
    if (formData.enable3DS && !validateAcquirerBins()) {
      setValidationError(t('atLeastOneBinRequired'));
      return;
    }

    // Ensure baseCurrency is in supportedCurrencies
    const supportedCurrencies = formData.supportedCurrencies.includes(formData.baseCurrency)
      ? formData.supportedCurrencies
      : [...formData.supportedCurrencies, formData.baseCurrency];

    // Convert NONE to empty string for backend
    const amexRequestorType = formData.amexRequestorType === 'NONE' ? '' : formData.amexRequestorType;

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'nmi',
          name: formData.name,
          enabled: formData.enabled,
          baseCurrency: formData.baseCurrency,
          supportedCurrencies,
          monthlyProcessingCapacity: moneyStringOrNumberToMinorUnits(
            formData.monthlyProcessingCapacity,
            formData.baseCurrency,
          ),
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            apiKey: formData.apiKey,
            nmiProcessorId: formData.nmiProcessorId.trim(),
            useProcessorToken: formData.useProcessorToken,
            enable3DS: formData.enable3DS,
            merchantName: formData.merchantName,
            merchantUrl: formData.merchantUrl,
            merchantCountryCode: formData.merchantCountryCode,
            amexRequestorType,
            cbSiretNumber: formData.cbSiretNumber,
            discoverClientId: formData.discoverClientId,
            acquirerBinVisa: formData.acquirerBinVisa,
            acquirerBinMastercard: formData.acquirerBinMastercard,
            acquirerBinAmex: formData.acquirerBinAmex,
            acquirerBinDiscover: formData.acquirerBinDiscover,
            acquirerBinJcb: formData.acquirerBinJcb,
            acquirerBinDinersClub: formData.acquirerBinDinersClub,
            acquirerBinCB: formData.acquirerBinCB,
            descriptor: {
              descriptor: formData.descriptor,
              mcc: formData.mcc,
              merchantId: formData.merchantId,
            },
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      const parsed = parseTRPCError(error);
      // Backend message is a fixed English sentinel (processors.ts's manageError
      // matches the same prefix) wrapping the NMI-specific reason — extract it so
      // the surrounding sentence can be localized instead of shown in raw English.
      const nmiRejection = parsed.message.match(/^NMI rejected these credentials \(([^)]+)\)\./);
      const description = nmiRejection
        ? t('nmiCredentialsRejected', { reason: nmiRejection[1] })
        : parsed.message || t('failedToSave');
      toast({ description, variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('editNMIConfiguration')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Currency Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('currencySettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="supportedCurrencies">{t('supportedCurrencies')}</Label>
              <MultiSelect
                options={CURRENCY_OPTIONS}
                selected={formData.supportedCurrencies}
                onChange={(value) => setFormData({ ...formData, supportedCurrencies: value })}
                placeholder={t('searchForCurrency')}
              />
            </div>

            <div>
              <Label htmlFor="baseCurrency">{t('baseCurrency')}</Label>
              <Select
                value={formData.baseCurrency}
                onValueChange={(value) => setFormData({ ...formData, baseCurrency: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('selectBaseCurrency')} />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_CURRENCIES.map((currency) => (
                    <SelectItem key={currency} value={currency}>
                      {currency}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="monthlyProcessingCapacity">{t('monthlyProcessingCapacity')}</Label>
              <Input
                id="monthlyProcessingCapacity"
                type="number"
                min={0}
                value={formData.monthlyProcessingCapacity}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    monthlyProcessingCapacity: Math.max(0, Number(e.target.value)),
                  })
                }
                placeholder="0"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('monthlyProcessingCapacityDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  {t('testModeDescription')}{' '}
                  <a
                    href="https://secure.networkmerchants.com/gw/merchants/resources/integration/integration_portal.php#testing_information"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-900 underline hover:text-yellow-950"
                  >
                    {t('viewTestCards')}
                  </a>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* NMI Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('nmiSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <SecretInput
              label={t('apiKey')}
              value={formData.apiKey}
              onChange={(value) => setFormData({ ...formData, apiKey: value })}
              placeholder={t('enterApiKey')}
            />
            <div>
              <Label htmlFor="nmiProcessorId">{t('nmiProcessorId')}</Label>
              <Input
                id="nmiProcessorId"
                value={formData.nmiProcessorId}
                onChange={(e) => setFormData({ ...formData, nmiProcessorId: e.target.value })}
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('nmiProcessorIdDescription')}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useProcessorToken"
                checked={formData.useProcessorToken}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, useProcessorToken: checked === true })
                }
              />
              <Label htmlFor="useProcessorToken">{t('useCustomerVault')}</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enable3DS"
                checked={formData.enable3DS}
                onCheckedChange={(checked) => setFormData({ ...formData, enable3DS: checked === true })}
              />
              <Label htmlFor="enable3DS">{t('enableUniversal3DS')}</Label>
            </div>

            {formData.enable3DS && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-700">{t('universal3DSDescription')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3DS Configuration */}
        {formData.enable3DS && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{t('universal3DSConfiguration')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="merchantName">{t('merchantName')}</Label>
                  <Input
                    id="merchantName"
                    value={formData.merchantName}
                    onChange={(e) => setFormData({ ...formData, merchantName: e.target.value })}
                    placeholder={t('merchantNamePlaceholder')}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">{t('merchantNameDescription')}</p>
                </div>

                <div>
                  <Label htmlFor="merchantUrl">{t('merchantUrl')}</Label>
                  <Input
                    id="merchantUrl"
                    value={formData.merchantUrl}
                    onChange={(e) => setFormData({ ...formData, merchantUrl: e.target.value })}
                    placeholder="https://www.yourcompany.com"
                  />
                  <p className="text-muted-foreground mt-1 text-xs">{t('merchantUrlDescription')}</p>
                </div>

                <div>
                  <Label htmlFor="merchantCountryCode">{t('merchantCountryCode')}</Label>
                  <Input
                    id="merchantCountryCode"
                    value={formData.merchantCountryCode}
                    onChange={(e) => setFormData({ ...formData, merchantCountryCode: e.target.value })}
                    placeholder="e.g., 840 for USA, 250 for France"
                    maxLength={3}
                  />
                  <p className="text-muted-foreground mt-1 text-xs">{t('merchantCountryCodeDescription')}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{t('networkSpecificInfo')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="amexRequestorType">{t('amexRequestorType')}</Label>
                  <Select
                    value={formData.amexRequestorType || 'NONE'}
                    onValueChange={(value) => setFormData({ ...formData, amexRequestorType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('selectAmexRequestorType')} />
                    </SelectTrigger>
                    <SelectContent>
                      {AMEX_REQUESTOR_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground mt-1 text-xs">{t('amexRequestorTypeDescription')}</p>
                </div>

                <div>
                  <Label htmlFor="cbSiretNumber">{t('cbSiretNumber')}</Label>
                  <Input
                    id="cbSiretNumber"
                    value={formData.cbSiretNumber}
                    onChange={(e) => setFormData({ ...formData, cbSiretNumber: e.target.value })}
                    placeholder="e.g., 12345678901234"
                  />
                  <p className="text-muted-foreground mt-1 text-xs">{t('cbSiretNumberDescription')}</p>
                </div>

                <div>
                  <Label htmlFor="discoverClientId">{t('discoverClientId')}</Label>
                  <Input
                    id="discoverClientId"
                    value={formData.discoverClientId}
                    onChange={(e) => setFormData({ ...formData, discoverClientId: e.target.value })}
                    placeholder="e.g., DISC12345"
                  />
                  <p className="text-muted-foreground mt-1 text-xs">{t('discoverClientIdDescription')}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{t('acquirerBINs')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="acquirerBinVisa">{t('visaAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinVisa"
                    value={formData.acquirerBinVisa}
                    onChange={(e) => setFormData({ ...formData, acquirerBinVisa: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>

                <div>
                  <Label htmlFor="acquirerBinMastercard">{t('mastercardAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinMastercard"
                    value={formData.acquirerBinMastercard}
                    onChange={(e) => setFormData({ ...formData, acquirerBinMastercard: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>

                <div>
                  <Label htmlFor="acquirerBinAmex">{t('amexAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinAmex"
                    value={formData.acquirerBinAmex}
                    onChange={(e) => setFormData({ ...formData, acquirerBinAmex: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>

                <div>
                  <Label htmlFor="acquirerBinDiscover">{t('discoverAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinDiscover"
                    value={formData.acquirerBinDiscover}
                    onChange={(e) => setFormData({ ...formData, acquirerBinDiscover: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>

                <div>
                  <Label htmlFor="acquirerBinJcb">{t('jcbAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinJcb"
                    value={formData.acquirerBinJcb}
                    onChange={(e) => setFormData({ ...formData, acquirerBinJcb: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>

                <div>
                  <Label htmlFor="acquirerBinDinersClub">{t('dinersClubAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinDinersClub"
                    value={formData.acquirerBinDinersClub}
                    onChange={(e) => setFormData({ ...formData, acquirerBinDinersClub: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>

                <div>
                  <Label htmlFor="acquirerBinCB">{t('cbAcquirerBin')}</Label>
                  <Input
                    id="acquirerBinCB"
                    value={formData.acquirerBinCB}
                    onChange={(e) => setFormData({ ...formData, acquirerBinCB: e.target.value })}
                    placeholder="e.g., 123456"
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Descriptor Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('descriptorSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="descriptor">{t('statementDescriptor')}</Label>
              <Input
                id="descriptor"
                value={formData.descriptor}
                onChange={(e) => setFormData({ ...formData, descriptor: e.target.value })}
                placeholder="YOURCOMPANY.COM"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('statementDescriptorDescription')}</p>
            </div>

            <div>
              <Label htmlFor="mcc">{t('merchantCategoryCode')}</Label>
              <Input
                id="mcc"
                value={formData.mcc}
                onChange={(e) => setFormData({ ...formData, mcc: e.target.value })}
                placeholder="e.g., 5812"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('mccDescription')}</p>
            </div>

            <div>
              <Label htmlFor="merchantId">{t('merchantId')}</Label>
              <Input
                id="merchantId"
                value={formData.merchantId}
                onChange={(e) => setFormData({ ...formData, merchantId: e.target.value })}
                placeholder="e.g., ABCD1234"
              />
              <p className="text-muted-foreground mt-1 text-xs">{t('merchantIdDescription')}</p>
            </div>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const NMIEditPageContainer = withRequiredPermission(NMIEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
